/**
 * Привязки при черчении и перетаскивании.
 *
 * При черчении стен (`wallBodies`) стены «ловят» конец новой стены всем своим телом, а не
 * только осью: стены толстые, и по картинке их доводят до кромки, а не до середины.
 *   • Курсор рядом с углом — в пределах толщины его стен — конец встаёт ровно в угол:
 *     контур замыкается, даже если клик лёг чуть мимо, и косого хвостика не остаётся.
 *   • Курсор в толщине стены (или чуть за кромкой) — конец встаёт на её ось (Т-стык): перегородку, доведённую
 *     до кромки наружной стены, не надо вымерять до середины. Если стену ведут от точки,
 *     конец встаёт туда, где направление черчения пересекает ось, — стена остаётся прямой.
 *   • Прямая стена важнее угла (`aimAtNode`). Стену ведут ровно (шаг 15°), а угол под курсором
 *     лежит чуть в стороне от этого направления — к нему стена ушла бы наискось. Тогда:
 *     свободный конец соседней стены дотягивается вдоль своей оси до линии новой (`stretch`) —
 *     выходит чистый угол; у настоящего угла (сходятся две стены и больше) новая стена встаёт
 *     ровно Т-стыком в стену рядом. Наискось в угол — только если её и ведут под этим углом.
 */
import {
	angleOf,
	dist,
	len,
	norm,
	projectOnSegment,
	snapTo,
	sub,
	type Pt,
} from "@/shared/lib/geometry";
import { MIN_WALL_LENGTH } from "./planOps";
import type { WallGraph } from "./types-def";

export type SnapKind = "node" | "wall" | "align" | "grid" | "free";

export interface SnapGuides {
	x?: number;
	y?: number;
}

export interface SnapResult {
	point: Pt;
	kind: SnapKind;
	guides: SnapGuides;
	/**
	 * Свободный конец соседней стены (`nodeId`, сейчас в `from`) при постройке переедет
	 * в `point` вдоль своей оси: стены сойдутся чистым углом, а новая останется прямой
	 */
	stretch?: { nodeId: string; from: Pt };
}

export interface SnapOptions {
	gridStep: number;
	nodeTolerance: number;
	from?: Pt | null;
	exclude?: string | null;
	freeform?: boolean;
	/**
	 * Черчение стен: ловить конец телом стен — угол в пределах толщины его стен, стену —
	 * в пределах её толщины. При перетаскивании узла не нужно: узел не врезается в стену
	 */
	wallBodies?: boolean;
	/**
	 * Притягивать к сетке (по умолчанию да). Нет — при обводке подложки: углы плана на картинке
	 * с сеткой не совпадают, и точка встаёт туда, где кликнули. Привязки к углам, стенам,
	 * выравнивание и шаг угла остаются — стены выходят ровными
	 */
	grid?: boolean;
}

const ANGLE_STEP = Math.PI / 12;
const DEG = Math.PI / 180;
/**
 * Горизонталь и вертикаль ловятся широко: стены квартиры почти все под прямым углом, и на
 * короткой стене рука легко уводит курсор на 10–15°, — с шагом 15° стена прыгала на 15°
 */
const ORTHO_CAPTURE = 15 * DEG;
/** Диагональ 45° — уже, остальные шаги по 15° — только когда стену явно ведут под этим углом */
const DIAGONAL_CAPTURE = 7.5 * DEG;
/** Насколько направление на угол может отличаться от ровного, чтобы стена шла прямо в угол */
const STRAIGHT_SLACK = 0.3 * DEG;

/** Ровный угол для направления: 0/90° — с запасом, 45° — поуже, иначе ближайшие 15°. */
const steadyAngle = (angle: number): number => {
	const quarter = Math.round(angle / (Math.PI / 2)) * (Math.PI / 2);
	if (Math.abs(angle - quarter) <= ORTHO_CAPTURE) return quarter;
	const eighth = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
	if (Math.abs(angle - eighth) <= DIAGONAL_CAPTURE) return eighth;
	return Math.round(angle / ANGLE_STEP) * ANGLE_STEP;
};

/** Половина толщины самой толстой стены у каждого узла, мм: в этом радиусе узел ловит курсор. */
const nodeReach = (plan: WallGraph): Map<string, number> => {
	const reach = new Map<string, number>();
	for (const wall of Object.values(plan.walls)) {
		for (const id of [wall.a, wall.b]) {
			reach.set(id, Math.max(reach.get(id) ?? 0, wall.thickness / 2));
		}
	}
	return reach;
};

/** cos(90°) даёт 6e-17, а не ноль: без зачистки ровная стена ушла бы на волосок вбок. */
const clean = (v: number) => (Math.abs(v) < 1e-12 ? 0 : v);

/** Направление черчения от `from` к курсору — ровное (`steadyAngle`). */
const aimDirection = (from: Pt, world: Pt): Pt => {
	const angle = steadyAngle(angleOf(sub(world, from)));
	return { x: clean(Math.cos(angle)), y: clean(Math.sin(angle)) };
};

/**
 * Точка на оси стены, в толщину которой попал курсор; null — курсор не в стене.
 * Ведут стену от `from` — точка там, где направление черчения (с шагом угла) пересекает ось.
 */
const wallBodyPoint = (
	plan: WallGraph,
	world: Pt,
	from: Pt | null | undefined,
	/** Запас за кромкой стены, мм: клик ровно по кромке со стороны комнаты тоже ловится */
	margin: number,
): Pt | null => {
	const ray = from ? aimDirection(from, world) : null;
	let best: { point: Pt; distance: number } | null = null;
	for (const wall of Object.values(plan.walls)) {
		const a = plan.nodes[wall.a];
		const b = plan.nodes[wall.b];
		if (!a || !b) continue;
		const projection = projectOnSegment(world, a, b);
		if (projection.distance > wall.thickness / 2 + margin) continue;
		if (projection.t <= 0 || projection.t >= 1) continue;
		if (best && projection.distance >= best.distance) continue;
		let point = projection.point;
		if (from && ray) {
			// Пересечение направления черчения с осью стены — если оно в пределах стены
			const axis = sub(b, a);
			const denom = ray.x * axis.y - ray.y * axis.x;
			if (Math.abs(denom) > 1e-9) {
				const w = sub(a, from);
				const t = (w.x * axis.y - w.y * axis.x) / denom;
				const u = (w.x * ray.y - w.y * ray.x) / denom;
				const hit = { x: from.x + ray.x * t, y: from.y + ray.y * t };
				// Ведут почти вдоль стены — пересечение уходит далеко от курсора, берём ближайшую точку оси
				if (t > 0 && u > 0 && u < 1 && dist(hit, projection.point) <= wall.thickness) point = hit;
			}
		}
		best = { point, distance: projection.distance };
	}
	return best?.point ?? null;
};

/**
 * Курсор поймал угол `node`, а стену ведут от `from`. Если к углу стена пошла бы наискось
 * (направление дальше `STRAIGHT_SLACK` от ровного шага), держим ровное направление:
 *   • у свободного конца (одна стена) — конец дотягивается вдоль своей оси до линии новой стены;
 *   • у настоящего угла — Т-стык в стену рядом с ним (`wallBodyPoint`).
 * null — к углу и так ровно или ровно не получится: остаётся привязка к углу.
 */
const aimAtNode = (
	plan: WallGraph,
	world: Pt,
	from: Pt,
	node: { id: string; x: number; y: number },
	radius: number,
	margin: number,
): SnapResult | null => {
	const toNode = sub(node, from);
	if (len(toNode) < MIN_WALL_LENGTH) return null;
	const angle = angleOf(toNode);
	const deviation = Math.abs(angle - Math.round(angle / ANGLE_STEP) * ANGLE_STEP);
	if (deviation <= STRAIGHT_SLACK) return null;

	const ray = aimDirection(from, world);
	const walls = Object.values(plan.walls).filter(
		(wall) => wall.a === node.id || wall.b === node.id,
	);
	if (walls.length === 1) {
		const wall = walls[0];
		const other = plan.nodes[wall.a === node.id ? wall.b : wall.a];
		if (!other) return null;
		const axis = norm(sub(node, other));
		// Где ровная линия новой стены пересекает ось этой стены
		const denom = ray.x * axis.y - ray.y * axis.x;
		if (Math.abs(denom) < 1e-6) return null;
		const w = sub(other, from);
		const t = (w.x * axis.y - w.y * axis.x) / denom;
		if (t <= MIN_WALL_LENGTH) return null;
		const point = { x: from.x + ray.x * t, y: from.y + ray.y * t };
		const along = (point.x - other.x) * axis.x + (point.y - other.y) * axis.y;
		// Стена не укорачивается до нуля и не уезжает далеко: конец едет лишь рядом с собой
		if (along < MIN_WALL_LENGTH) return null;
		if (dist(point, node) > Math.max(radius * 2, wall.thickness)) return null;
		return {
			point,
			kind: "node",
			guides: {},
			stretch: { nodeId: node.id, from: { x: node.x, y: node.y } },
		};
	}
	const onWall = wallBodyPoint(plan, world, from, margin);
	if (onWall && dist(onWall, node) >= MIN_WALL_LENGTH)
		return { point: onWall, kind: "wall", guides: {} };
	return null;
};

export const snapPoint = (plan: WallGraph, world: Pt, options: SnapOptions): SnapResult => {
	const { gridStep, nodeTolerance, from, exclude, freeform, wallBodies, grid = true } = options;
	const nodes = Object.values(plan.nodes).filter((n) => n.id !== exclude);
	const reach = wallBodies ? nodeReach(plan) : null;

	let nearest: { id: string; x: number; y: number; radius: number } | null = null;
	let nearestD = Infinity;
	for (const node of nodes) {
		const d = dist(node, world);
		const radius = Math.max(nodeTolerance, reach?.get(node.id) ?? 0);
		if (d < radius && d < nearestD) {
			nearestD = d;
			nearest = { id: node.id, x: node.x, y: node.y, radius };
		}
	}
	if (nearest) {
		// Черчение стены: ровное направление важнее угла чуть в стороне (Alt — без привязок)
		if (from && wallBodies && !freeform) {
			const straight = aimAtNode(plan, world, from, nearest, nearest.radius, nodeTolerance);
			if (straight) return straight;
		}
		return { point: { x: nearest.x, y: nearest.y }, kind: "node", guides: {} };
	}

	if (freeform) return { point: world, kind: "free", guides: {} };

	if (wallBodies) {
		const onWall = wallBodyPoint(plan, world, from, nodeTolerance);
		if (onWall) return { point: onWall, kind: "wall", guides: {} };
	}

	const alignTolerance = nodeTolerance * 1.2;

	if (from) {
		const vector = sub(world, from);
		const length = len(vector);
		if (length > gridStep / 2) {
			const dir = aimDirection(from, world);
			const rayLength = Math.max(gridStep, length);
			const onRay = (t: number): Pt => ({ x: from.x + dir.x * t, y: from.y + dir.y * t });

			let bestHit: { t: number; guides: SnapGuides; delta: number } | undefined;
			const consider = (t: number, guides: SnapGuides) => {
				if (t <= 0) return;
				const delta = Math.abs(t - rayLength);
				if (delta > alignTolerance) return;
				if (!bestHit || delta < bestHit.delta) bestHit = { t, guides, delta };
			};
			for (const node of nodes) {
				if (Math.abs(dir.x) > 1e-6) consider((node.x - from.x) / dir.x, { x: node.x });
				if (Math.abs(dir.y) > 1e-6) consider((node.y - from.y) / dir.y, { y: node.y });
			}
			if (bestHit) {
				return { point: onRay(bestHit.t), kind: "align", guides: bestHit.guides };
			}

			if (!grid) {
				// Без сетки — длина ровно до курсора вдоль выбранного направления
				const along = vector.x * dir.x + vector.y * dir.y;
				return { point: onRay(Math.max(1, along)), kind: "free", guides: {} };
			}
			return {
				point: onRay(Math.max(gridStep, snapTo(rayLength, gridStep))),
				kind: "grid",
				guides: {},
			};
		}
	}

	const guides: SnapGuides = {};
	let x = world.x;
	let y = world.y;
	let bestX = alignTolerance;
	let bestY = alignTolerance;
	for (const node of nodes) {
		const dx = Math.abs(node.x - world.x);
		if (dx < bestX) {
			bestX = dx;
			x = node.x;
			guides.x = node.x;
		}
		const dy = Math.abs(node.y - world.y);
		if (dy < bestY) {
			bestY = dy;
			y = node.y;
			guides.y = node.y;
		}
	}
	if (guides.x === undefined && grid) x = snapTo(world.x, gridStep);
	if (guides.y === undefined && grid) y = snapTo(world.y, gridStep);

	return {
		point: { x, y },
		kind: guides.x !== undefined || guides.y !== undefined ? "align" : grid ? "grid" : "free",
		guides,
	};
};
